/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { Api, ApiLogin, ID_OPERACAO_ORCAMENTO } from './plataforma';
import { conexao } from './conexao';
import { inscreverModoBusca, modoBuscaAtual } from './modoBusca';
import { dbReplaceAll, dbUpsertMany, dbCount, kvGet, kvSet, kvDelete, TStore } from './db';
import { invalidarCache, adicionarParceiroLocal } from './cadastros';
import { listarFila, removerDaFila, atualizarItemFila, usuarioLogado, IItemFila } from './fila';
import { limparSessaoOffline, guardarSessaoOnline, guardarCaixas } from './sessaoOffline';
import { renovarLoginOnline, tokenAtualEhOffline } from './loginOffline';
import { guardarEmpresaImpressao } from './vendaOffline';

// Sincronização do PDV offline, nos dois sentidos:
//  1. envia a fila de lançamentos feitos sem internet (abertura de caixa,
//     parceiros novos, vendas), na ordem em que foram feitos;
//  2. baixa uma cópia completa dos cadastros usados na venda (produtos,
//     referências, parceiros, plano de contas, operações, contas, vendedores)
//     e os usuários da empresa pro login offline (ver loginOffline.ts).
// Mesmo código no PDVSIAC e no FrontSIAC — cada um envia também o que o
// outro lançou, quando os dois usam o mesmo banco local (ver db.ts).
// Roda sozinha ao abrir o PDV, a cada volta da conexão e periodicamente
// (ver iniciarSincronizacaoAutomatica); e manualmente pelo painel de
// sincronização no PDV.

const TAMANHO_PAGINA = 1000;
const TIMEOUT_DOWNLOAD_MS = 120000;
const INTERVALO_AUTOMATICO_MS = 30 * 60 * 1000;
// De quanto em quanto tempo confere se algum produto mudou no servidor (ver
// verificarAlteracaoProdutos) — consulta leve, só baixa algo se mudou.
const INTERVALO_VERIFICAR_PRODUTOS_MS = 30 * 1000;
// A sincronização incremental pede "o que mudou desde a última vez" usando o
// relógio DESTE computador; recua um pouco pra não perder alteração se o
// relógio daqui estiver adiantado em relação ao servidor (regravar o que já
// estava igual não faz mal).
const MARGEM_DESDE_MS = 10 * 60 * 1000;
// Última "assinatura" do cadastro de produtos já refletida na cópia local
// (ver verificarAlteracaoProdutos).
const CHAVE_ASSINATURA_PRODUTOS = 'assinaturaProdutos';
// Também serve de "desde" pra sincronização incremental — ver baixarCadastros.
const CHAVE_ULTIMA_SYNC = 'ultimaSincronizacao';
// Empresa (idempresa do token) de quem sincronizou por último NESTE
// dispositivo — ver limparTudoOffline().
const CHAVE_EMPRESA_SINCRONIZADA = 'idempresaSincronizada';

export interface IEstadoSincronizacao {
  sincronizando: boolean;
  etapa: string;
  ultimaSincronizacao?: string;
  erro?: string;
}

let estado: IEstadoSincronizacao = { sincronizando: false, etapa: '' };
const ouvintes = new Set<(estado: IEstadoSincronizacao) => void>();

const atualizar = (parcial: Partial<IEstadoSincronizacao>) => {
  estado = { ...estado, ...parcial };
  ouvintes.forEach((ouvinte) => ouvinte(estado));
};

export const estadoSincronizacao = () => estado;
export const inscreverSincronizacao = (ouvinte: (estado: IEstadoSincronizacao) => void) => {
  ouvintes.add(ouvinte);
  return () => { ouvintes.delete(ouvinte); };
};

kvGet<string>(CHAVE_ULTIMA_SYNC).then((ultima) => {
  if (ultima) atualizar({ ultimaSincronizacao: ultima });
});

const mensagemErro = (erro: unknown): string => {
  if (axios.isAxiosError(erro)) {
    const dados: any = erro.response?.data;
    const erros = dados?.errors || dados?.error;
    if (erros?.default) return erros.default;
    if (erros && typeof erros === 'object') {
      const mensagens = Object.values(erros).filter((valor) => typeof valor === 'string');
      if (mensagens.length) return mensagens.join(' | ');
    }
    if (erro.response) return `Erro ${erro.response.status} no servidor`;
    return 'Sem conexão com o servidor';
  }
  return erro instanceof Error ? erro.message : 'Erro inesperado';
};

// Marca as requisições da sincronização: o interceptor do PDV desloga em
// qualquer 404, e uma rota que ainda não existe no servidor (backend
// desatualizado) não pode derrubar a sessão de quem está vendendo.
const SILENCIOSO: Record<string, unknown> = { silencioso: true };

// ---------- 1. Envio da fila ----------

// Venda da tela de vendas recusada antes de ir pro servidor por falta de
// estoque — erro de negócio (não de conexão): o item fica marcado como erro
// na fila com essa mensagem, e a venda aparece em vermelho na listagem pra
// ser corrigida (ver ListagemDeVenda/DetalheDeVenda).
class SemEstoqueError extends Error {}

// Empresa que controla estoque: antes de enviar uma venda da tela de vendas
// (que não seja Orçamento), confere no servidor se ainda há estoque de cada
// item — a venda foi feita offline, com o estoque da cópia local, e o do
// servidor pode ter mudado desde então. Faltando, não envia.
const conferirEstoqueVendaFront = async (item: IItemFila) => {
  if (!usuarioLogado().est) return;
  const [entrada, itens] = item.payload || [];
  if (!entrada || entrada.idoperacao === ID_OPERACAO_ORCAMENTO) return;
  const lista = (Array.isArray(itens) ? itens : [])
    .map((linha: any) => ({ idprodutos: linha.idprodutos, qtde: Number(linha.qtds) || 0 }))
    .filter((linha: { idprodutos: string; qtde: number }) => linha.idprodutos && linha.qtde > 0);
  if (!lista.length) return;
  const { data } = await Api.post<{ descricao: string; qtdesolicitada: number; estoquedisponivel: number }[]>(
    '/produtos/verificarestoque', { itens: lista }, SILENCIOSO,
  );
  if (Array.isArray(data) && data.length) {
    const detalhes = data
      .map((faltando) => `${faltando.descricao || 'produto'} (pedido ${faltando.qtdesolicitada}, disponível ${faltando.estoquedisponivel})`)
      .join('; ');
    throw new SemEstoqueError(`Sem estoque suficiente: ${detalhes}. Ajuste os itens e salve a venda de novo.`);
  }
};

const enviarItem = async (item: IItemFila) => {
  const config = { headers: { idconta: item.idconta }, ...SILENCIOSO };
  if (item.tipo === 'abrirsessao') {
    const { data } = await Api.post('/sessaocaixa/abrir', item.payload, config);
    await limparSessaoOffline();
    await guardarSessaoOnline(data);
    // A abertura offline usava um idsessao local (o id deste item); agora
    // a sessão existe no servidor com outro id — as vendas dela ainda na
    // fila passam a apontar pra ele (ver listarVendasPdvOffline).
    if (data?.idsessao) {
      for (const venda of await listarFila()) {
        if (venda.tipo === 'venda' && venda.extra?.idsessao === item.id) {
          await atualizarItemFila({ ...venda, extra: { ...venda.extra, idsessao: data.idsessao } });
        }
      }
    }
    return;
  }
  if (item.tipo === 'parceiro') {
    await Api.post('/parceiros', item.payload, config);
    return;
  }
  if (item.tipo === 'vendafront') {
    await conferirEstoqueVendaFront(item);
    await Api.post('/vendas', item.payload, SILENCIOSO);
    return;
  }
  await Api.post('/vendaspdv', item.payload, config);
};

// Devolve false se parou por falta de conexão (o resto fica pra próxima).
const enviarFila = async (): Promise<boolean> => {
  const { iduser } = usuarioLogado();
  // Só os lançamentos do usuário logado: o servidor abre a sessão e vincula
  // a venda pelo usuário do token de quem envia (os de outro usuário
  // esperam ele entrar de novo neste PDV).
  const itens = (await listarFila()).filter((item) => !item.iduser || item.iduser === iduser);
  for (let indice = 0; indice < itens.length; indice++) {
    const item = itens[indice];
    atualizar({ etapa: `Enviando lançamentos offline (${indice + 1}/${itens.length})...` });
    try {
      await enviarItem(item);
      await removerDaFila(item.id);
    } catch (erro) {
      // Sem resposta do servidor: para e deixa o resto pra próxima. (No
      // FrontSIAC o interceptor troca o erro de rede por um Error comum, por
      // isso também confere o estado da conexão, que ele marca antes.)
      if (conexao.estaOffline() || (axios.isAxiosError(erro) && !erro.response)) return false;
      // Erro de negócio (ex.: caixa com sessão de outro usuário, validação)
      // — fica marcado na fila com a mensagem, e segue com os próximos.
      // Relê da fila: o item pode ter mudado durante esta sincronização (ex.:
      // venda remarcada pra sessão real quando a abertura offline subiu) — a
      // cópia do início do laço sobrescreveria isso.
      const atual = (await listarFila()).find((registro) => registro.id === item.id) || item;
      await atualizarItemFila({ ...atual, status: 'erro', erro: mensagemErro(erro), tentativas: atual.tentativas + 1 });
    }
  }
  return true;
};

// Envia na hora UMA venda da fila (ex.: o usuário abriu uma venda feita
// offline e clicou em Salvar, já com internet), pelo mesmo caminho da
// sincronização (enviarItem: mesmo idmov, o backend ignora reenvio). Se
// houver lançamentos anteriores na fila dos quais ela pode depender (ex.:
// parceiro cadastrado offline), roda a sincronização completa, que envia
// tudo na ordem certa. Retorna:
//  - 'enviada': chegou ao servidor e saiu da fila;
//  - 'semconexao': continua na fila, será enviada quando a conexão voltar;
//  - Error: o servidor recusou (fica marcada como erro na fila, com o motivo).
export const enviarVendaDaFila = async (id: string): Promise<'enviada' | 'semconexao' | Error> => {
  if (emAndamento) await emAndamento.catch(() => undefined);
  const fila = await listarFila();
  const item = fila.find((registro) => registro.id === id);
  if (!item) return 'enviada';

  const temDependencia = fila.some((registro) => registro.tipo === 'parceiro' || registro.tipo === 'abrirsessao');
  if (temDependencia) {
    await sincronizar();
    const depois = (await listarFila()).find((registro) => registro.id === id);
    if (!depois) return 'enviada';
    if (depois.status === 'erro') return new Error(depois.erro || 'O servidor recusou a venda.');
    return 'semconexao';
  }

  try {
    await enviarItem(item);
    await removerDaFila(item.id);
    return 'enviada';
  } catch (erro) {
    if (conexao.estaOffline() || (axios.isAxiosError(erro) && !erro.response)) return 'semconexao';
    const mensagem = mensagemErro(erro);
    await atualizarItemFila({ ...item, status: 'erro', erro: mensagem, tentativas: item.tentativas + 1 });
    return new Error(mensagem);
  }
};

// ---------- 2. Download dos cadastros ----------

const baixarPaginado = async (montarUrl: (pagina: number) => string): Promise<any[]> => {
  const todos: any[] = [];
  for (let pagina = 1; ; pagina++) {
    const { data } = await Api.get(montarUrl(pagina), { timeout: TIMEOUT_DOWNLOAD_MS, ...SILENCIOSO });
    const lista = Array.isArray(data) ? data : [];
    todos.push(...lista);
    if (lista.length < TAMANHO_PAGINA) return todos;
  }
};

// `&desde=...` só entra quando existe — omitido, os endpoints devolvem tudo
// (sincronização completa), do jeito que sempre funcionou.
const paramDesde = (desde?: string) => (desde ? `&desde=${encodeURIComponent(desde)}` : '');

const CADASTROS: { store: TStore; nome: string; incremental?: boolean; baixar: (desde?: string) => Promise<any[]> }[] = [
  {
    store: 'produtos',
    nome: 'produtos',
    incremental: true,
    baixar: async (desde) => {
      const produtos = await baixarPaginado((p) => `/produtos?page=${p}&limit=${TAMANHO_PAGINA}&filter=&tipo=descricao&modobusca=contem${paramDesde(desde)}`);
      // Cadastro completo (grupo, fiscal, preços...) pra tela de detalhe do
      // produto abrir offline no FrontSIAC — mesclado na mesma linha. Se
      // falhar (sem permissão, servidor sem a rota), fica só a listagem.
      try {
        atualizar({ etapa: 'Baixando cadastro completo dos produtos...' });
        const completos = await baixarPaginado((p) => `/produtosoffline?page=${p}&limit=${TAMANHO_PAGINA}${paramDesde(desde)}`);
        const porId = new Map(completos.map((produto) => [produto.idproduto, produto]));
        return produtos.map((produto) => ({ ...porId.get(produto.idproduto), ...produto }));
      } catch {
        return produtos;
      }
    },
  },
  {
    store: 'referencias',
    nome: 'referências',
    // Sem coluna de atualização própria (referência nasce/morre junto do
    // produto) — sempre baixa tudo. Pela rota leve /referenciasoffline
    // (paginada pela chave, estoque só da página); backend antigo, sem essa
    // rota, cai na busca por referência de sempre.
    baixar: async () => {
      try {
        return (await baixarPaginado((p) => `/referenciasoffline?page=${p}&limit=${TAMANHO_PAGINA}`))
          .filter((referencia) => referencia.idref);
      } catch {
        return (await baixarPaginado((p) => `/produtos?page=${p}&limit=${TAMANHO_PAGINA}&filter=&tipo=referencia&modobusca=contem`))
          .filter((referencia) => referencia.idref);
      }
    },
  },
  {
    store: 'parceiros',
    nome: 'parceiros',
    incremental: true,
    baixar: (desde) => baixarPaginado((p) => `/parceiroslist?page=${p}&limit=${TAMANHO_PAGINA}&filter=&tipo=nome${paramDesde(desde)}`),
  },
  {
    store: 'planocontas',
    nome: 'plano de contas',
    incremental: true,
    baixar: (desde) => baixarPaginado((p) => `/planocontalist?page=${p}&limit=${TAMANHO_PAGINA}&filter=${paramDesde(desde)}`),
  },
  {
    store: 'operacoes',
    nome: 'operações',
    incremental: true,
    // Essa rota não pagina (sempre página 1) — um limite alto traz todas.
    baixar: async (desde) => {
      const { data } = await Api.get(`/estoqueoperacoeslist?limit=100000&filter=&tipo=3${paramDesde(desde)}`, { timeout: TIMEOUT_DOWNLOAD_MS, ...SILENCIOSO });
      return Array.isArray(data) ? data : [];
    },
  },
  {
    store: 'contas',
    nome: 'contas',
    incremental: true,
    baixar: (desde) => baixarPaginado((p) => `/contabancarialist?page=${p}&limit=${TAMANHO_PAGINA}&filter=${paramDesde(desde)}`),
  },
  {
    store: 'vendedores',
    nome: 'vendedores',
    // Cadastro de usuário não tem atualizadoem — sempre baixa tudo (tabela pequena).
    baixar: () => baixarPaginado((p) => `/userslist?page=${p}&limit=${TAMANHO_PAGINA}&filter=`),
  },
];

// Usuários da empresa de quem está logado (com o hash da senha) pro login
// offline — vêm do servidor de login. Não derruba a sincronização se falhar
// (ex.: servidor de login ainda sem a rota /usuariosoffline): o login
// offline só continua com a cópia anterior.
const baixarUsuarios = async () => {
  atualizar({ etapa: 'Baixando usuários para login offline...' });
  try {
    const { data } = await ApiLogin.get('/usuariosoffline', { timeout: TIMEOUT_DOWNLOAD_MS });
    if (Array.isArray(data) && data.length) await dbReplaceAll('usuarios', data);
  } catch {
    // ver comentário acima
  }
};

// `desde` presente + cadastro marcado `incremental` → baixa só quem mudou e
// GRAVA POR CIMA (dbUpsertMany), preservando o resto da cópia local. Sem
// `desde` (primeira sincronização, catálogo vazio ou troca de empresa) ou
// cadastro sem suporte a incremental (referencias/vendedores, que sempre
// voltam completos) → baixa tudo e substitui a store inteira (dbReplaceAll),
// igual sempre funcionou.
const baixarCadastros = async (desde?: string) => {
  for (const cadastro of CADASTROS) {
    atualizar({ etapa: `Baixando ${cadastro.nome}...` });
    const incremental = !!desde && !!cadastro.incremental;
    const registros = await cadastro.baixar(incremental ? desde : undefined);
    if (incremental) {
      await dbUpsertMany(cadastro.store, registros);
    } else {
      await dbReplaceAll(cadastro.store, registros);
    }
    invalidarCache(cadastro.store);
  }

  // Parceiros criados offline e ainda não enviados não vêm do servidor —
  // devolve eles pra cópia local, senão sumiriam da busca até enviar.
  const pendentes = (await listarFila()).filter((item) => item.tipo === 'parceiro');
  for (const item of pendentes) {
    await adicionarParceiroLocal({ ...item.payload, status: true, pendente: true });
  }

  await baixarUsuarios();
  await baixarEmpresa();
  await baixarTabelasDeProduto();

  atualizar({ etapa: 'Baixando caixas...' });
  try {
    const { data } = await Api.get('/caixaturno/caixas', SILENCIOSO);
    if (Array.isArray(data)) await guardarCaixas(data);
  } catch {
    // Lista de caixas é complementar — não invalida a sincronização.
  }
};

// Dados da empresa pro cabeçalho do comprovante de vendas feitas offline
// (mesmo formato de /vendasimpressao). A logo é buscada uma vez aqui só
// pra ficar guardada pelo service worker (cache de /empresalogs/) — o
// comprovante busca ela pela mesma URL e, offline, recebe a cópia guardada.
const baixarEmpresa = async () => {
  atualizar({ etapa: 'Baixando dados da empresa...' });
  try {
    const { data } = await Api.get('/empresaimpressao', SILENCIOSO);
    if (!Array.isArray(data) || !data.length) return;
    await guardarEmpresaImpressao(data);
    const logo = data[0]?.logo;
    if (logo) {
      await fetch(`${window.env.REACT_APP_URL_IMGLOG || ''}${logo}`, { mode: 'cors' }).catch(() => undefined);
    }
  } catch {
    // Complementar — sem isso o comprovante offline usa a última empresa guardada.
  }
};

// Grupos, subgrupos e CFOP — só pra tela de detalhe do produto (FrontSIAC)
// mostrar os nomes offline. Tabelas pequenas, guardadas inteiras. O
// subgrupo só é listado por grupo no servidor, então vai grupo a grupo.
const baixarTabelasDeProduto = async () => {
  atualizar({ etapa: 'Baixando grupos, subgrupos e CFOP...' });
  try {
    const { data: grupos } = await Api.get('/grupolist?limit=100000&tipo=nome&filter=', { timeout: TIMEOUT_DOWNLOAD_MS, ...SILENCIOSO });
    if (Array.isArray(grupos)) {
      const subgrupos: any[] = [];
      for (const grupo of grupos) {
        const { data } = await Api.get(`/subgrupolist?limit=100000&grupo=${grupo.idgrupo}&filter=`, SILENCIOSO);
        if (Array.isArray(data)) subgrupos.push(...data.map((sub) => ({ idgrupo: grupo.idgrupo, ...sub })));
      }
      await kvSet('grupos', grupos);
      await kvSet('subgrupos', subgrupos);
    }
  } catch {
    // Complementar — sem isso os nomes de grupo/subgrupo ficam em branco offline.
  }
  try {
    const { data: cfop } = await Api.get('/cfop?tipo=natureza&limit=100000&filter=', { timeout: TIMEOUT_DOWNLOAD_MS, ...SILENCIOSO });
    if (Array.isArray(cfop)) await kvSet('cfop', cfop);
  } catch {
    // idem
  }
};

// Zera TUDO que é offline neste dispositivo (cadastros, fila de lançamentos
// pendentes e checkpoints de sincronização) — chamado quando outro usuário,
// de outra empresa, loga aqui (ver sincronizar()). O IndexedDB é o mesmo
// banco pra qualquer empresa que use este computador/navegador (ver db.ts),
// então sem isso os cadastros (e eventuais vendas ainda não enviadas) de uma
// empresa ficariam visíveis — ou seriam enviados — sob a sessão da outra.
const ENTIDADES_CADASTRO: TStore[] = ['produtos', 'referencias', 'parceiros', 'planocontas', 'operacoes', 'contas', 'usuarios', 'vendedores'];

const limparTudoOffline = async () => {
  for (const store of ENTIDADES_CADASTRO) await dbReplaceAll(store, []);
  await dbReplaceAll('fila', []);
  await kvDelete(CHAVE_ULTIMA_SYNC);
  await kvDelete(CHAVE_ASSINATURA_PRODUTOS);
  await kvDelete('grupos');
  await kvDelete('subgrupos');
  await kvDelete('cfop');
  invalidarCache();
};

// ---------- Orquestração ----------

let emAndamento: Promise<void> | null = null;

interface IAssinaturaProdutos {
  ultimaalteracao: string | null;
  quantidade: number;
}

// Última alteração + quantidade de produtos no servidor (GET
// /produtosultimaalteracao). null se não deu pra consultar (sem conexão,
// sem permissão, servidor ainda sem a rota) — aí só não detecta mudança.
const consultarAssinaturaProdutos = async (): Promise<IAssinaturaProdutos | null> => {
  try {
    const { data } = await Api.get('/produtosultimaalteracao', SILENCIOSO);
    return data && typeof data.quantidade === 'number' ? data : null;
  } catch {
    return null;
  }
};

// `completo`: baixa todos os cadastros inteiros (sem "desde") — usado quando
// algum produto foi EXCLUÍDO no servidor, o que a busca incremental não pega.
export const sincronizar = (opcoes: { cadastros?: boolean; completo?: boolean } = {}): Promise<void> => {
  if (emAndamento) return emAndamento;
  emAndamento = (async () => {
    atualizar({ sincronizando: true, erro: undefined, etapa: 'Verificando conexão...' });
    try {
      if (conexao.estaOffline() && !(await conexao.verificar())) {
        atualizar({ erro: 'Sem conexão com o servidor.' });
        return;
      }
      const { iduser, idempresa } = usuarioLogado();
      if (!iduser) return;

      // Outro usuário, de OUTRA empresa, logou neste mesmo dispositivo —
      // zera tudo que é offline antes de enviar ou baixar qualquer coisa
      // (ver limparTudoOffline). Sem `idempresa` (token antigo, campo pode
      // não existir) não dá pra comparar com segurança, então não mexe.
      if (idempresa) {
        const empresaAnterior = await kvGet<string>(CHAVE_EMPRESA_SINCRONIZADA);
        if (empresaAnterior && empresaAnterior !== idempresa) {
          atualizar({ etapa: 'Outra empresa detectada — limpando dados offline deste computador...' });
          await limparTudoOffline();
        }
        await kvSet(CHAVE_EMPRESA_SINCRONIZADA, idempresa);
      }

      // Entrou offline (token local): antes de qualquer envio, troca pelo
      // token do servidor — com o token local o servidor recusaria tudo
      // (401) e o app deslogaria no meio da sincronização.
      if (tokenAtualEhOffline() && !(await renovarLoginOnline())) {
        atualizar({ erro: 'Você entrou sem internet. Saia e entre de novo, agora com internet, para enviar os lançamentos offline.' });
        return;
      }

      const completo = await enviarFila();
      if (!completo) {
        atualizar({ erro: 'A conexão caiu durante o envio — o restante será enviado quando voltar.' });
        return;
      }

      const ultima = await kvGet<string>(CHAVE_ULTIMA_SYNC);
      const catalogoVazio = (await dbCount('produtos').catch(() => 0)) === 0;
      const vencida = !ultima || Date.now() - new Date(ultima).getTime() > INTERVALO_AUTOMATICO_MS;
      if (opcoes.cadastros || opcoes.completo || catalogoVazio || vencida) {
        // Assinatura lida ANTES de baixar: se um produto mudar durante o
        // download, a próxima verificação ainda enxerga a diferença.
        const assinatura = await consultarAssinaturaProdutos();
        // Catálogo vazio (primeira vez, ou acabou de zerar por troca de
        // empresa) ou pedido completo: baixa tudo, sem "desde" — não dá pra
        // confiar num checkpoint antigo quando a cópia local está vazia.
        const desde = catalogoVazio || opcoes.completo || !ultima
          ? undefined
          : new Date(new Date(ultima).getTime() - MARGEM_DESDE_MS).toISOString();
        await baixarCadastros(desde);
        const agora = new Date().toISOString();
        await kvSet(CHAVE_ULTIMA_SYNC, agora);
        if (assinatura) await kvSet(CHAVE_ASSINATURA_PRODUTOS, assinatura);
        atualizar({ ultimaSincronizacao: agora });
      }
    } catch (erro) {
      atualizar({ erro: `Falha na sincronização: ${mensagemErro(erro)}` });
    } finally {
      atualizar({ sincronizando: false, etapa: '' });
      emAndamento = null;
    }
  })();
  return emAndamento;
};

// Atualização automática quando algum produto muda no servidor: compara a
// assinatura atual (última alteração + quantidade) com a da cópia local.
// Mudou → atualiza; produto excluído (quantidade menor) → cópia completa.
// Nada mudou → não baixa nada.
export const verificarAlteracaoProdutos = async () => {
  if (emAndamento || conexao.estaOffline() || modoBuscaAtual() === 'offline' || !usuarioLogado().iduser) return;
  if (tokenAtualEhOffline()) return;
  const atual = await consultarAssinaturaProdutos();
  if (!atual) return;
  const anterior = await kvGet<IAssinaturaProdutos>(CHAVE_ASSINATURA_PRODUTOS);
  if (anterior && anterior.ultimaalteracao === atual.ultimaalteracao && anterior.quantidade === atual.quantidade) return;
  const excluiuProduto = !!anterior && atual.quantidade < anterior.quantidade;
  await sincronizar({ cadastros: true, completo: excluiuProduto });
};

// Chamado uma vez pelo PDV depois do login. Devolve a função de limpeza.
// Ao entrar: atualiza os cadastros na hora (na primeira vez neste computador
// baixa tudo; depois, só o que mudou). Depois disso, confere a cada
// INTERVALO_VERIFICAR_PRODUTOS_MS se algum produto mudou no servidor.
// Com o modo Offline escolhido no seletor (ver modoBusca.ts), a
// sincronização automática fica parada — o usuário pediu pra trabalhar só
// neste computador, então as vendas ficam na fila (visíveis e editáveis
// localmente) em vez de sumirem dela por um envio em segundo plano. Voltando
// pra Automático/Online, sincroniza na hora. O "Sincronizar agora" do
// PainelSincronizacao continua funcionando a qualquer momento.
export const iniciarSincronizacaoAutomatica = () => {
  const sincronizarSePermitido = () => {
    if (modoBuscaAtual() !== 'offline') sincronizar();
  };
  if (modoBuscaAtual() !== 'offline') sincronizar({ cadastros: true });
  const cancelarConexao = conexao.inscrever((online) => {
    if (online) sincronizarSePermitido();
  });
  const cancelarModo = inscreverModoBusca((modo) => {
    if (modo !== 'offline' && conexao.estaOnline()) sincronizar();
  });
  const intervalo = setInterval(() => {
    if (conexao.estaOnline()) sincronizarSePermitido();
  }, INTERVALO_AUTOMATICO_MS);
  const intervaloProdutos = setInterval(() => {
    verificarAlteracaoProdutos();
  }, INTERVALO_VERIFICAR_PRODUTOS_MS);
  return () => {
    cancelarConexao();
    cancelarModo();
    clearInterval(intervalo);
    clearInterval(intervaloProdutos);
  };
};
